'use client';

import Link from '~/(tenant)/_components/tenant-link';

import {
  ChevronsUpDown,
  Home,
  LogOut,
  Settings,
  Calendar,
} from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { If } from '@kit/ui/if';
import { SubMenuModeToggle } from '@kit/ui/mode-toggle';
import { ProfileAvatar } from '@kit/ui/profile-avatar';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';
import { usePersonalAccountData } from '@kit/accounts/hooks/use-personal-account-data';
import { useTranslation } from 'react-i18next';

import { useNativePresentation } from './native/_lib/use-native-presentation';

interface UserData {
  id: string;
  email?: string;
  phone?: string;
}

export function UserAccountDropdown({
  className,
  user,
  signOutRequested,
  showProfileName = true,
  features,
}: {
  user: UserData;
  signOutRequested: () => unknown;
  features: {
    enableThemeToggle: boolean;
  };
  showProfileName?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const presentation = useNativePresentation();
  const { data: personalAccountData } = usePersonalAccountData(user.id);

  // Prefer email, fall back to name then phone. `||` (not `??`) on purpose:
  // GoTrue serializes a missing email as '' — email-skipped phone customers
  // would otherwise render a BLANK "Signed in as" line even though they have
  // a perfectly good name and phone on file.
  const formattedPhone = user?.phone
    ? user.phone.startsWith('+')
      ? user.phone
      : `+${user.phone}`
    : '';
  const signedInAsLabel =
    personalAccountData?.email ||
    user?.email ||
    personalAccountData?.name ||
    formattedPhone ||
    '';
  const displayName =
    personalAccountData?.name || user?.email || formattedPhone || '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('common:openProfileMenuAriaLabel')}
        data-test={'account-dropdown-trigger'}
        className={cn(
          'animate-in fade-in focus:outline-primary flex cursor-pointer items-center duration-500',
          className ?? '',
          {
            ['active:bg-secondary/50 items-center gap-4 rounded-md' +
            ' hover:bg-secondary p-2 transition-colors']: showProfileName,
          },
        )}
      >
        <ProfileAvatar
          className={'rounded-md'}
          fallbackClassName={'rounded-md border'}
          displayName={displayName}
          pictureUrl={personalAccountData?.picture_url}
        />

        <If condition={showProfileName}>
          <div className={'fade-in animate-in flex w-full flex-col truncate text-left'}>
            <span data-test={'account-dropdown-display-name'} className={'truncate text-sm'}>
              {displayName}
            </span>

            <span data-test={'account-dropdown-email'} className={'text-muted-foreground truncate text-xs'}>
              {signedInAsLabel}
            </span>
          </div>

          <ChevronsUpDown className={'text-muted-foreground me-1 h-8'} />
        </If>
      </DropdownMenuTrigger>

      <DropdownMenuContent {...presentation} className={cn('xl:min-w-[15rem]!', presentation.className)}>
        <DropdownMenuItem className={'h-10! rounded-none'}>
          <div className={'flex flex-col justify-start truncate text-left text-xs'}>
            <div className={'text-muted-foreground'}>
              <Trans i18nKey={'common:signedInAs'} />
            </div>
            <div>
              <span className={'block truncate'}>{signedInAsLabel}</span>
            </div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link className={'s-full flex cursor-pointer items-center space-x-2'} href={'/'}>
            <Home className={'h-5'} />
            <span>
              <Trans i18nKey={'common:routes.home'} />
            </span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link className={'s-full flex cursor-pointer items-center space-x-2'} href={'/profile'}>
            <Settings className={'h-5'} />
            <span><Trans i18nKey={'common:memberClub.nav.profileSettings'} /></span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link className={'s-full flex cursor-pointer items-center space-x-2'} href={'/bookings'}>
            <Calendar className={'h-5'} />
            <span><Trans i18nKey={'common:bookings'} /></span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <If condition={features.enableThemeToggle}>
          <SubMenuModeToggle />
        </If>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          data-test={'account-dropdown-sign-out'}
          role={'button'}
          className={'cursor-pointer'}
          onClick={signOutRequested}
        >
          <span className={'flex w-full items-center space-x-2'}>
            <LogOut className={'h-5'} />
            <span>
              <Trans i18nKey={'auth:signOut'} defaults="Sign out" />
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}